import { useEffect, useState } from 'react';
import { FCW } from '../../types.ts';
import { FormContext } from '@/form/FormContext.tsx';
import { ConfigSlideId, ConfigTranslatedText } from '@/eventConfig/types.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { equals, isNil, omit } from 'ramda';
import { andBoolArray, orBooleanArray } from '@/form/utils.tsx';
import { FormPayload, FormPayloadMeta, PayloadTimeline } from '@/form/types.ts';
import { i18n } from '@/translation/TranslationProvider.tsx';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
import { useStaffNotes } from '@/sidePanel/useStaffNotes.ts';
import { useFieldValidation } from './fields/_pipeline/useValidateField.ts';

export type FieldType = string | number | boolean | null;

type Payload = Partial<FormPayload>;

export type FieldValue = {
  raw: FieldType;
  normalized: FieldType;
};

export interface FieldStatus {
  value: FieldValue;
  isValid: boolean;
  slideId: ConfigSlideId;
  hint?: ConfigTranslatedText;
}

export type FormDataType = Record<string, FieldStatus>;

export type FormGetFieldValueFn = (fieldName: string, type?: 'raw' | 'normalized') => FieldType | undefined;
export type FormGetFieldStatusFn = (fieldName: string) => FieldStatus | undefined;
export type FormOnChangeFn = (fieldName: string, value: FieldStatus | null) => void;

export interface FormApi {
  formData: FormDataType;
  onChange: FormOnChangeFn;
  getFieldValue: FormGetFieldValueFn;
  getFieldStatus: FormGetFieldStatusFn;
  isEmpty: () => boolean;
  reset: () => void;
  getFormData: () => FormDataType;
  getFormPayload: () => FormPayloadMeta & Payload;
  validateSlide: (slideId: ConfigSlideId) => boolean;
}

export const FormProvider: FCW = ({ children }) => {
  const START_SLIDE_ID: ConfigSlideId = 'home';
  const { currentSlide, setSlideValid } = useNavigation();
  const [formData, setFormData] = useState<FormDataType>({});
  const { eventConfig } = useEventConfig();
  const { getTimeline } = useNavigation();
  const { staffNotes } = useStaffNotes();

  const getFieldValue: FormGetFieldValueFn = (fieldName, type = 'normalized') => {
    return formData?.[fieldName]?.value?.[type];
  };

  const getFieldStatus: FormGetFieldStatusFn = (fieldName) => {
    return formData?.[fieldName];
  };

  const { getIsRequiredValidationResult } = useFieldValidation(getFieldValue, getFieldStatus);

  useEffect(() => {
    if (currentSlide?.id === START_SLIDE_ID) {
      // We just jumped home, reset the Data
      reset();
    }
  }, [currentSlide]);

  // This effect watches the "currentSlide" and the "formData".
  // Whenever any of these change, we run the "is slide valid" check
  useEffect(() => {
    if (!currentSlide?.id) {
      return;
    }
    const slideValid = validateSlide(currentSlide.id);
    setSlideValid(slideValid);

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [currentSlide?.id, formData]);

  const onChange = (fieldName: string, status: FieldStatus | null) => {
    // Unset value with "null"
    if (isNil(status)) {
      setFormData((v) => {
        return omit([fieldName], v);
      });
    } else {
      setFormData((v) => {
        return {
          ...v,
          [fieldName]: { ...status },
        };
      });
    }
  };

  // Validate slide calls every field for the given Slide and
  // when all fields that have a validator are valid, we return true
  const validateSlide = (slideId: ConfigSlideId): boolean => {
    if (!eventConfig) {
      return false;
    }

    const slide = eventConfig.slides[slideId];
    if (!slide) {
      return false;
    }

    const fields = slide.fields ?? [];

    if (slide.type === 'checkandsaveregistration') {
      return false;
    }

    const fieldValidity = fields.map(({ name, validator }) => {
      const isValid = formData?.[name]?.isValid ?? getIsRequiredValidationResult(validator, getFieldStatus, name);

      return {
        fieldName: name,
        isValid,
      };
    });

    // Some slides define how to combine field-validations into a slide validation. 'and' is default
    const combinator = slide?.validator?.type ?? 'and';
    const references = slide?.validator?.references ?? [];

    if (combinator === 'and') {
      // && the validities
      return andBoolArray(fieldValidity.map(({ isValid }) => isValid));
    }
    if (combinator === 'or') {
      // 'or' combinator might reference ALL fields (when .references is empty) or
      // Just a few of the given values.

      if (references.length === 0) {
        // OR combine them all
        return orBooleanArray(fieldValidity.map(({ isValid }) => isValid));
      } else {
        // Split the array into valus that are to be OR'd and into values that are to be AND'ed

        const andValues = fieldValidity.filter(({ fieldName }) => !references.includes(fieldName));
        const orValues = fieldValidity.filter(({ fieldName }) => references.includes(fieldName));

        const orResult = orValues.reduce((innerAccumulator, { isValid }) => innerAccumulator || isValid, false);
        const andResult = andValues.reduce((innerAccumulator, { isValid }) => innerAccumulator && isValid, true);
        return orResult && andResult;
      }
    }
    return false;
  };

  const reset = () => {
    setFormData({});
  };

  const isEmpty = () => {
    return equals(formData, {});
  };

  const getFormData = () => {
    return formData;
  };

  const getFormPayload = (): FormPayloadMeta & Payload => {
    const withEventData = {
      _locale: i18n.locale,
      _event: eventConfig?.meta,
      _notes: staffNotes,
      _duration: 0,
      _timeline: [] as PayloadTimeline[],
    };

    try {
      const timeline = getTimeline();
      if (timeline.length > 0) {
        withEventData._duration = moment().diff(timeline[0].start, 'seconds');
        withEventData._timeline = timeline.map((item) => {
          return {
            slide: item.slide,
            start: item.start.format(), // ISO-8601
            finish: item.finish.format(), // ISO-8601
            duration: item.duration,
          };
        });
      }
    } catch (e) {
      console.debug('Error calculating slide-view-timeline.');
      Sentry.captureException(e);
    }

    const formPayload = Object.keys(formData).reduce((acc, curr) => {
      let value = formData[curr].value.normalized ?? formData[curr].value.raw;

      if (curr === 'birthdate' && typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        value = moment(value, 'DD.MM.YYYY').format('YYYY-MM-DD');
      }

      if (isNil(value)) {
        // Skip field values, that are null.
        return acc;
      }
      return { ...acc, [curr]: value };
    }, {});

    return { ...formPayload, ...withEventData };
  };

  return (
    <FormContext.Provider
      value={{
        formData,
        onChange,
        getFieldValue,
        isEmpty,
        reset,
        getFormData,
        getFormPayload,
        validateSlide,
        getFieldStatus,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
